import { useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { useSelector } from 'react-redux';
import { makeStyles } from '@mui/styles';
import { useFirestore } from 'react-redux-firebase';
import collections from '../../../constants/FirestoreCollections';
import { IconButton, Badge, Tooltip, useMediaQuery } from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import { Box } from '@mui/system';
import { useAudio } from '../../../hooks/useAudio';
import AlertPopover from './AlertPopover';

const useStylesPopUp = makeStyles((theme) => ({
  customPopup: {
    margin: '20px',
    color: '#333 !important',
    borderRadius: '10px !important',
    boxShadow: '0 4px 8px rgba(255, 204, 1, 0.5) !important',
    border: '2px solid rgba(255, 204, 1, 0.5) !important',
    position: 'relative !important',
  },
  customCloseButton: {
    position: 'absolute !important',
    top: '10px !important',
    right: '10px !important',
    color: '#333 !important',
    fontSize: '2rem !important',
    '&:hover': {
      color: 'rgba(255, 0, 0, 0.8) !important',
    },
  },
  customTitle: {
    marginRight: '20px !important',
    fontSize: '1.15rem !important',
    fontWeight: 'bold !important',
    color: '#333 !important',
    textAlign: 'start !important',
  },
  customText: {
    fontSize: '1rem !important',
    color: '#333 !important',
  },
  customIcon: {
    border: 'none !important',
    display: 'flex !important',
    alignItems: 'center !important',
    justifyContent: 'center !important',
    width: '50px !important',
    height: '50px !important',
    margin: '0 auto !important',
  },
}));

const useStylesNotification = (isMobile) =>
  makeStyles((theme) => ({
    notificactionClicked: {
      color: theme.palette.primary.main,
    },
    notificaction: {
      color: !isMobile ? theme.palette.white.main : '',
    },
  }));

const MySwal = withReactContent(Swal);

const AlertComponent = () => {
  const isMobile = useMediaQuery('(max-width:600px)');
  const classesPopUp = useStylesPopUp();
  const classesNotification = useStylesNotification(isMobile)();

  const organization = useSelector((state) => state.app.organization);
  const prestoConfig = useSelector((state) => state.app.prestobotsConfig);
  const profile = useSelector((state) => state.firebase.profile);
  const { play } = useAudio(prestoConfig?.defaultChatAlertMP3 || '');
  const firestore = useFirestore();

  const memoizedValues = useMemo(() => {
    const chatAlerts = organization?.configuration?.chatAlerts;

    return {
      alertStates: chatAlerts?.states || [],
      alertIntervalInMinutes: chatAlerts?.intervalMinutes || 10,
      isChatAlertsEnabled: chatAlerts?.isChatAlertsEnabled || false,
      isSoundAlertsEnabled: chatAlerts?.isSoundAlertsEnabled || false,
    };
  }, [organization?.configuration?.chatAlerts]);

  const statusDictionary = useMemo(() => {
    if (!prestoConfig?.states || !Array.isArray(prestoConfig.states)) {
      return {};
    }

    return prestoConfig.states.reduce((acc, item) => {
      acc[item.key] = { label: item.label, color: item.color };
      return acc;
    }, {});
  }, [prestoConfig?.states]);

  const { alertStates, alertIntervalInMinutes, isChatAlertsEnabled, isSoundAlertsEnabled } =
    memoizedValues;

  const [anchorEl, setAnchorEl] = useState(null);
  const [chatAlertEnabled, setChatAlertEnabled] = useState(isChatAlertsEnabled);
  const [alertSoundEnabled, setAlertSoundEnabled] = useState(isSoundAlertsEnabled);
  const [notificationPermission, setNotificationPermission] = useState('default');
  const statusPorHacer = 0;

  const activatedStates = alertStates?.map((id) => statusDictionary && statusDictionary[id]);

  const intervalMinutes = alertIntervalInMinutes || 10; // MUCHO MUY IMPORTANTE ESTA VALIDACION

  const intervalTime = Number(intervalMinutes) * 60 * 1000;

  useEffect(() => {
    const fetchChatsAndAlert = async () => {
      const chats = await getTodayChatsByOrganizationId();
      const filteredChats = filterChats(chats, alertStates, statusPorHacer);
      const popUpText = generateNotificationText(filteredChats);

      if (filteredChats.length > 0) {
        const title = `Tiene ${filteredChats.length} chats pendientes`;
        showAlert(title, popUpText);
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        // iOS no soporta notificaciones en PWA sin instalación en pantalla de inicio, ni tampoco soporta que se ejecuten en segundo plano una vez instalada. A menos que se use envien notificaciones push desde un servidor al endpoint del usuario.
        if (!isIOS) await handleSystemNotification(title, popUpText);
      }
    };
    if (chatAlertEnabled) {
      const interval = setInterval(() => {
        fetchChatsAndAlert();
      }, intervalTime);

      return () => clearInterval(interval);
    }
  }, [chatAlertEnabled, intervalTime, alertSoundEnabled, alertStates]);

  useEffect(() => {
    setChatAlertEnabled(isChatAlertsEnabled);
    setAlertSoundEnabled(isSoundAlertsEnabled);
    if (isChatAlertsEnabled) {
      requestNotificationPermission();
    }
  }, [isChatAlertsEnabled, isSoundAlertsEnabled]);

  const requestNotificationPermission = async () => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (isIOS) {
      if (notificationPermission !== 'isNotIOSSupported') {
        setNotificationPermission('isNotIOSSupported');
      }
      return;
    }
    // eslint-disable-next-line no-undef
    if (Notification.permission === 'default') {
      // eslint-disable-next-line no-undef
      await Notification.requestPermission();
    }
    // eslint-disable-next-line no-undef
    setNotificationPermission(Notification.permission);
  };
  const handleSystemNotification = async (title, text) => {
    // eslint-disable-next-line no-undef
    if (Notification.permission === 'granted') {
      await showSystemNotification(title, text);
    }
  };

  const showSystemNotification = async (title, text) => {
    const isMobile = /Mobi|Android/i.test(navigator.userAgent);
    const options = {
      body: stripHTMLTags(text),
      icon: '/logo512.png',
    };

    const optionsMobile = {
      body: stripHTMLTags(text),
      icon: '/logo512.png',
      requireInteraction: true,
      actions: [{ action: 'dismiss', title: 'Descartar' }],
    };

    if ('serviceWorker' in navigator && isMobile) {
      navigator.serviceWorker.getRegistration().then(async (registration) => {
        if (!registration) {
          try {
            registration = await navigator.serviceWorker.register('/service-worker.js');
            console.log('showSystemNotification SW Registro exitoso');

            // Esperar hasta que el Service Worker esté activo
            await navigator.serviceWorker.ready;
          } catch (error) {
            console.error('showSystemNotification Falló el registro del SW:', error);
            return;
          }
        }
        // Enviar la notificación
        registration.showNotification(title, optionsMobile);
      });
    } else {
      // eslint-disable-next-line no-undef
      const notification = new Notification(title, options);
      notification.onclick = () => window.focus();
    }
  };

  const showAlert = (title, text) => {
    try {
      alertSoundEnabled && play();
      MySwal.fire({
        title,
        html: text?.replace(/\n/g, '<br>'),
        iconHtml:
          '<img src="/logo512.png" alt="Prestobots icono" style="width: 50px; height: 50px; display: block; margin: 0 auto;" >',
        toast: true,
        position: 'bottom-end',
        showConfirmButton: false,
        showCloseButton: true,
        timer: 0,
        customClass: {
          icon: classesPopUp.customIcon,
          popup: classesPopUp.customPopup,
          closeButton: classesPopUp.customCloseButton,
          title: classesPopUp.customTitle,
          text: classesPopUp.customText,
        },
        buttonsStyling: false,
      });
    } catch (error) {
      console.error('Error displaying alert:', error.message);
    }
  };

  const filterChats = (chats, estadosDeseados, targetStatus) => {
    try {
      const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);

      if (!Array.isArray(chats)) {
        throw new Error("El parámetro 'chats' debe ser un arreglo.");
      }
      if (!Array.isArray(estadosDeseados)) {
        throw new Error("El parámetro 'estadosDeseados' debe ser un arreglo.");
      }
      return chats?.filter((chat) => {
        if (!chat.updatedAt || (!chat.status && chat.status !== 0)) {
          console.warn("El chat no tiene la propiedad 'updatedAt' o 'status'.");
          return false;
        }

        const updatedAtDate = new Date(
          chat.updatedAt.seconds * 1000 + chat.updatedAt.nanoseconds / 1000000,
        );
        if (isNaN(updatedAtDate)) {
          console.warn("La fecha 'updatedAt' no es válida.");
          return false;
        }
        const isDesiredStatus = estadosDeseados.includes(chat.status);
        // Si es un chat en por hacer la alerta sale a la hora recien.
        const isTargetStatusAndOld = chat.status === targetStatus && updatedAtDate < oneHourAgo;

        if (isDesiredStatus) {
          const isAnAssociatedStatus = filterChatsByAssociatedStatus(chat);
          return (
            isDesiredStatus &&
            (isTargetStatusAndOld || chat.status !== targetStatus) &&
            isAnAssociatedStatus
          );
        }
        return isDesiredStatus && (isTargetStatusAndOld || chat.status !== targetStatus);
      });
    } catch (error) {
      console.error('filterChats', error.message);
      return [];
    }
  };

  const filterChatsByAssociatedStatus = (chat) => {
    if (!organization || !profile || !chat) return true;
    try {
      const { configuration } = organization;
      const chatStatesConfig = configuration?.chatStates?.userAssociatedStates;
      const associatedStates = chatStatesConfig?.associatedStates || [];
      const isRestricted = chatStatesConfig?.enabled;
      const profileHasClaims = profile?.claims && profile.claims.length > 0;
      if (!isRestricted || profileHasClaims) return true;
      const associatedState = associatedStates.find(({ state }) => state.key === chat.status);
      return associatedState
        ? associatedState.users.some((user) => user.email === profile.email)
        : true;
    } catch (error) {
      console.error('filterChatsByAssociatedStatus', error);
      return true;
    }
  };

  const stripHTMLTags = (input) => {
    try {
      const tempElement = document.createElement('div');

      tempElement.innerHTML = input;

      return tempElement.textContent || tempElement.innerText || '';
    } catch (error) {
      console.error('stripHTMLTags', error);
      return '';
    }
  };
  const generateNotificationText = (chats) => {
    try {
      // Contador de chats por estado
      const statusCounts = chats?.reduce((acc, chat) => {
        acc[chat.status] = (acc[chat.status] || 0) + 1;
        return acc;
      }, {});

      // Generar lista de texto
      const notificationText = Object.keys(statusCounts).map((status) => {
        const count = statusCounts[status];
        const statusInfo = statusDictionary[status];
        if (!statusInfo) {
          console.warn(`No se encuentra la información para el estado '${status}'.`);
          return `Hay <strong>${count}</strong> chats en estado desconocido.`;
        }
        const { label, color } = statusInfo;
        const statusLabel = `<span style="color: ${color};">${label}</span>`;
        const statusText =
          parseInt(status) === 0
            ? `Hay <strong>${count}</strong> chats en ${statusLabel} con más de 1 hora`
            : `Hay  <strong>${count}</strong> chats en ${statusLabel}`;
        return statusText;
      });

      return notificationText.join('\n');
    } catch (error) {
      console.error('generateNotificationText', error.message || error);
      return `Error al generar el texto de notificación`;
    }
  };

  const getTodayChatsByOrganizationId = async () => {
    const now = new Date();
    const startOfDay = new Date(now.setHours(0, 0, 0, 0));
    const endOfDay = new Date(now.setHours(23, 59, 59, 999));

    try {
      const snapshot = await firestore
        .collection(collections.chats)
        .where('organizationId', '==', organization.id)
        .where('updatedAt', '>=', startOfDay)
        .where('updatedAt', '<=', endOfDay)
        .get();

      if (snapshot.empty) {
        console.error('getTodayChatsByOrganizationId >> No matching documents.');
        return [];
      }

      const chats = snapshot.docs.map((doc) => ({
        status: doc.data().status,
        updatedAt: doc.data().updatedAt,
        // lastMessage: doc.data().messages.at(-1), // TODO: para mas adelante cuando sean notificaciones por usuario
        // session: doc.data().session, // TODO: para mas adelante cuando se quiera resaltar el chat
      }));

      return chats;
    } catch (error) {
      console.error('getTodayChatsByOrganizationId', error);
      return [];
    }
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleToggleChange = async (field, value) => {
    try {
      firestore
        .collection(collections.organizations)
        .doc(organization.id)
        .update({
          [`configuration.chatAlerts.${field}`]: value,
        })
        .then(() => {
          if (field === 'isChatAlertsEnabled') {
            setChatAlertEnabled(value);
          } else if (field === 'isSoundAlertsEnabled') {
            setAlertSoundEnabled(value);
          }
        });
    } catch (error) {
      console.error('handleToggleChange', error);
    }
  };

  return (
    <>
      {!isMobile ? (
        <IconButton
          onClick={handleClick}
          size="large"
          className={
            chatAlertEnabled
              ? `${classesNotification.notificactionClicked}`
              : `${classesNotification.notificaction}`
          }
        >
          <Tooltip title="Alertas chats" arrow>
            <Badge
              badgeContent={
                chatAlertEnabled ? (
                  alertSoundEnabled ? (
                    <VolumeUpIcon style={{ fontSize: '1rem' }} />
                  ) : (
                    <VolumeOffIcon style={{ fontSize: '1rem' }} />
                  )
                ) : (
                  ''
                )
              }
            >
              <NotificationsIcon />
            </Badge>
          </Tooltip>
        </IconButton>
      ) : (
        <Box
          p={2}
          className={
            chatAlertEnabled
              ? `${classesNotification.notificactionClicked}`
              : `${classesNotification.notificaction}`
          }
          onClick={handleClick}
        >
          <Badge
            color="primary"
            badgeContent={
              chatAlertEnabled ? (
                alertSoundEnabled ? (
                  <VolumeUpIcon style={{ fontSize: '1rem' }} />
                ) : (
                  <VolumeOffIcon style={{ fontSize: '1rem' }} />
                )
              ) : null
            }
          >
            <NotificationsIcon />
          </Badge>
        </Box>
      )}
      <AlertPopover
        anchorEl={anchorEl}
        handleClose={handleClose}
        chatAlertEnabled={chatAlertEnabled}
        alertSoundEnabled={alertSoundEnabled}
        handleToggleChange={handleToggleChange}
        activatedStates={activatedStates}
        notificationPermission={notificationPermission}
      />
    </>
  );
};

export default AlertComponent;
